// Drawer «Invitar en lote» de Administración · Equipo y accesos (handoff
// §3, plan 2026-10-30 §2.3, T-b7bh): dos pasos sobre las tres rutas de
// /organizations/me/invitations/batch.
//
// Paso 1: la Dropzone (XLSX o CSV, un archivo, 5 MB como el multipart de
// core-api) y «Descargar plantilla» (el .xlsx que genera el servidor con
// los roles que este caller puede otorgar). «Revisar» sube la planilla a
// …/preview; el servidor no escribe nada y devuelve una fila validada por
// fila con datos. Un error de nivel archivo (413, 422, 429) se muestra
// aquí mismo, en es-CL; una planilla sin filas también se queda en este
// paso.
//
// Paso 2: el archivo con su conteo, los pills «n listas para enviar» /
// «n con error», una tarjeta por fila con error (las tres primeras y «y n
// filas más con error») y la tabla Fila · Correo · Rol · Acceso · Estado.
// «Enviar n invitaciones» manda a …/commit sólo las filas listas, con el
// payload exacto que devolvió el preview; el servidor las re-valida y las
// envía una a una por el mismo camino que «Invitar persona». Si alguna
// falla el drawer sigue abierto con esa fila marcada «No enviada» y su
// motivo, y «Enviar» vuelve a mandar sólo esas (las enviadas ya no
// viajan). Los correos enviados se recuerdan mientras el drawer vive:
// si el usuario vuelve al paso 1 y revisa la misma planilla (o una
// corregida), esas filas nacen «Enviada» y no se repiten — ni el preview
// ni auth-svc comprueban invitaciones pendientes al mismo correo. La
// pantalla pone el toast, invalida e ir a Invitaciones.
//
// Los mensajes por fila son finales en el servidor (el preview conoce la
// receta y el alcance del caller); aquí no se reescriben. Sin campo
// «Mensaje»: el correo de auth-svc no lo tiene.

import { useRef, useState } from "react";
import { Drawer } from "../../../components/Drawer";
import { Dropzone, formatBytes } from "../../../components/Dropzone";
import { Icon } from "../../../components/icons";
import {
  applyBatchCommit,
  BATCH_EMPTY_FILE,
  batchCardEyebrow,
  batchCommitBody,
  batchCommitErrorMessage,
  batchCounts,
  batchErrorCards,
  batchFileErrorMessage,
  batchFileLabel,
  batchRowsOf,
  batchRowStateLabel,
  batchRowStateTone,
  batchSendLabel,
  batchSentEmails,
  batchSummaryPills,
  batchTemplateErrorMessage,
  pendingBatchRows,
  type BatchOutcome,
  type BatchRowView,
  type EquipoWords,
} from "./model";
import { equipoApi } from "./api";
import { useInvitationBatchCommit, useInvitationBatchPreview } from "./queries";
import { FileIcon } from "./UploadIcon";

export type InvitarLoteDrawerProps = {
  words: EquipoWords;
  onClose: () => void;
  /** Tras cada commit, con o sin filas fallidas. */
  onDone: (outcome: BatchOutcome) => void;
};

/** Lo que acepta el servidor: `accept` del input y filtro al soltar. */
export const BATCH_ACCEPT_ATTR = ".xlsx,.csv";
/** maxIngestUploadBytes del multipart de core-api (app.ts). */
export const BATCH_MAX_BYTES = 5 * 1024 * 1024;
const TEMPLATE_FILE_NAME = "plantilla_invitaciones.xlsx";

type Step = 1 | 2;

export function InvitarLoteDrawer({ words: w, onClose, onDone }: InvitarLoteDrawerProps) {
  const [step, setStep] = useState<Step>(1);
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState<BatchRowView[]>([]);
  // Correos que ya salieron en un commit de este drawer (ver cabecera).
  const [sentEmails, setSentEmails] = useState<ReadonlySet<string>>(() => new Set());
  const [commitError, setCommitError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const preview = useInvitationBatchPreview();
  const commit = useInvitationBatchCommit();

  // El foco inicial cae en el <input type="file"> de la Dropzone (el
  // camino de teclado del handoff), no en la X del drawer. El componente
  // no expone ref, así que se busca dentro del contenedor al montar; el
  // ref callback corre antes del efecto de useDialogFocus que lo lee.
  const initialFocusRef = useRef<HTMLElement | null>(null);
  function captureDropInput(node: HTMLDivElement | null) {
    initialFocusRef.current = node?.querySelector<HTMLInputElement>('input[type="file"]') ?? null;
  }

  const busy = preview.isPending || commit.isPending;
  const pending = pendingBatchRows(rows).length;
  const counts = batchCounts(rows);
  const cards = batchErrorCards(rows);

  function onFiles(files: File[]) {
    const chosen = files[0];
    if (!chosen) return;
    setFile(chosen);
    setFileError(null);
  }

  async function revisar() {
    if (!file || busy) return;
    setFileError(null);
    try {
      const result = await preview.mutateAsync(file);
      if (result.rows.length === 0) {
        setFileError(BATCH_EMPTY_FILE);
        return;
      }
      setFileName(result.fileName);
      setRows(batchRowsOf(result, sentEmails));
      setCommitError(null);
      setStep(2);
    } catch (err) {
      setFileError(batchFileErrorMessage(err));
    }
  }

  async function enviar() {
    if (busy || pending === 0) return;
    setCommitError(null);
    try {
      const result = await commit.mutateAsync(batchCommitBody(rows));
      const next = applyBatchCommit(rows, result);
      setSentEmails((prev) => batchSentEmails(prev, result));
      onDone({
        sent: result.sent.length,
        omitted: batchCounts(next).error + result.failed.length,
        failed: result.failed.length,
      });
      if (result.failed.length === 0) {
        onClose();
        return;
      }
      setRows(next);
    } catch (err) {
      setCommitError(batchCommitErrorMessage(err));
    }
  }

  // Misma mecánica que la plantilla de carga masiva (AdminCargaMasiva):
  // blob → <a download> → revoke.
  async function descargarPlantilla() {
    if (downloading) return;
    setDownloading(true);
    setFileError(null);
    try {
      const { blob, filename } = await equipoApi.batchTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename ?? TEMPLATE_FILE_NAME;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setFileError(batchTemplateErrorMessage(err));
    } finally {
      setDownloading(false);
    }
  }

  function renderPaso1() {
    return (
      <>
        {fileError && (
          <p className="auth-error ag-mb16" role="alert">
            {fileError}
          </p>
        )}
        <div ref={captureDropInput}>
          <Dropzone
            title="Arrastra la planilla aquí"
            hint="XLSX o CSV · una fila por persona"
            accept={BATCH_ACCEPT_ATTR}
            maxSize={BATCH_MAX_BYTES}
            disabled={busy}
            buttonTitle="Abre el selector de archivos para elegir la planilla"
            onFiles={onFiles}
          />
        </div>
        {file && (
          <div className="ag-file ag-mt16">
            <FileIcon />
            <span className="ag-strong">{file.name}</span>
            <span className="cell-meta">{formatBytes(file.size)}</span>
          </div>
        )}
        <div className="ag-row ag-mt16">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            title="Descargar la planilla vacía con los roles que puedes otorgar"
            disabled={downloading || busy}
            aria-busy={downloading || undefined}
            onClick={() => void descargarPlantilla()}
          >
            <Icon.Download s={12} /> {downloading ? "Descargando…" : "Descargar plantilla"}
          </button>
          <span className="ag-muted">
            Columnas: correo, nombre, rol, {w.assetOneLower} o {w.groupOneLower}
          </span>
        </div>
      </>
    );
  }

  function renderPaso2() {
    return (
      <>
        <div className="ag-file">
          <FileIcon />
          <span className="ag-strong">{batchFileLabel(fileName, rows.length)}</span>
        </div>
        <div className="ag-summary">
          {batchSummaryPills(counts).map((p) => (
            <span key={p.text} className={`st-pill st-pill--${p.tone}`}>
              {p.text}
            </span>
          ))}
        </div>
        {commitError && (
          <p className="auth-error ag-mb16" role="alert">
            {commitError}
          </p>
        )}
        {cards.shown.map((r) => (
          <div key={r.row} className="workbook-confirm-card--warn ag-mb16">
            <div className="workbook-confirm-card__eyebrow">{batchCardEyebrow(r)}</div>
            <div className="ag-muted">{r.message}</div>
          </div>
        ))}
        {cards.moreLabel && <p className="ag-muted ag-mb16">{cards.moreLabel}</p>}
        <table className="dtable dtable--compact">
          <caption className="sr-only">Filas de la planilla</caption>
          <thead>
            <tr>
              <th scope="col">Fila</th>
              <th scope="col">Correo</th>
              <th scope="col">Rol</th>
              <th scope="col">Acceso</th>
              <th scope="col">Estado</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.row}>
                <td className="mono">{r.row}</td>
                <td className="cell-strong">{r.email || "—"}</td>
                <td>{r.roleLabel || "—"}</td>
                <td>
                  <span className="cell-meta">{r.accessLabel}</span>
                </td>
                <td>
                  <span className={`st-pill st-pill--${batchRowStateTone(r.state)}`}>
                    {batchRowStateLabel(r.state)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  return (
    <Drawer
      title="Invitar en lote"
      wide
      onClose={onClose}
      initialFocusRef={initialFocusRef}
      footer={
        step === 1 ? (
          <>
            <button
              type="button"
              className="btn btn--ghost"
              title="Cerrar sin invitar"
              disabled={busy}
              onClick={onClose}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn--primary"
              title="Subir la planilla y revisar cada fila antes de enviar"
              disabled={!file || busy}
              aria-busy={preview.isPending || undefined}
              onClick={() => void revisar()}
            >
              {preview.isPending ? "Revisando…" : "Revisar"}
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn btn--ghost"
              title="Volver a elegir la planilla"
              disabled={busy}
              onClick={() => setStep(1)}
            >
              Volver
            </button>
            <button
              type="button"
              className="btn btn--primary"
              title="Enviar el correo de invitación a cada fila lista"
              disabled={pending === 0 || busy}
              aria-busy={commit.isPending || undefined}
              onClick={() => void enviar()}
            >
              {commit.isPending ? "Enviando…" : batchSendLabel(pending)}
            </button>
          </>
        )
      }
    >
      {step === 1 ? renderPaso1() : renderPaso2()}
    </Drawer>
  );
}
